import React, { useState } from "react";
import { AlertTriangle, Eye, Ban, X } from "lucide-react";
import Modal from "./Modal";

// Shown when the user clicks "Complete Order" and the backend returns
// 409 UNUSED_PALLETS_EXIST with a list of reserved pallets that were
// issued but never linked to RP or shipment.
//
// User options:
//   1. Void Unused Tags and Complete Order  → re-POST with void_unused_pallets=true
//   2. Review Tags                          → scrolls to the Issued LT panel
//   3. Cancel Completion
//
// PLLs are never reused; voided pallets remain visible in audit/history.
export default function UnusedPalletsCompletionModal({
    pallets,
    busy,
    onVoidAndComplete,
    onReview,
    onCancel,
}) {
    const [confirmed, setConfirmed] = useState(false);

    return (
        <Modal
            isOpen
            onClose={onCancel}
            title="Unused Issued Load Tags Detected"
            data-testid="unused-pallets-modal"
        >
            <div className="space-y-4">
                <div className="text-sm border border-amber-300 bg-amber-50 text-amber-900 rounded-sm px-3 py-2 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                        This order has <b>{pallets.length}</b> unused issued
                        pallet tag{pallets.length === 1 ? "" : "s"}. These tags
                        were created/printed but were not used in production or
                        shipment.
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="odr-table" data-testid="unused-pallets-table">
                        <thead>
                            <tr>
                                <th>Pallet No.</th>
                                <th>Product</th>
                                <th className="text-right">Planned/Blank Qty</th>
                                <th>Status</th>
                                <th>Issued</th>
                                <th>By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pallets.map((p) => (
                                <tr
                                    key={p.id}
                                    data-testid={`unused-pallet-${p.pallet_number}`}
                                >
                                    <td className="odr-mono text-xs font-semibold">
                                        {p.pallet_number}
                                    </td>
                                    <td className="text-xs">{p.product || "—"}</td>
                                    <td className="text-right odr-mono">
                                        {p.planned_qty
                                            ? Number(p.planned_qty).toLocaleString()
                                            : "—"}
                                    </td>
                                    <td>
                                        <span className="text-[10px] font-semibold uppercase tracking-wider border border-amber-300 bg-amber-100 text-amber-800 px-2 py-[2px] rounded-sm">
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="text-xs odr-mono">
                                        {p.issued_at?.slice(0, 10) || "—"}
                                    </td>
                                    <td className="text-xs">{p.issued_by || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="text-xs text-gray-600 space-y-1">
                    <p>
                        Voiding will mark these tags as <b>voided</b> with the
                        canonical reason{" "}
                        <i>&ldquo;Unused issued tag voided during order completion&rdquo;</i>.
                    </p>
                    <p>
                        PLL numbers are <b>never reused</b>. The voided pallets
                        remain visible in audit/history and will not count as
                        produced or shipped.
                    </p>
                </div>

                <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="accent-red-700"
                        data-testid="unused-confirm-checkbox"
                    />
                    I understand these {pallets.length} pallet
                    {pallets.length === 1 ? "" : "s"} will be voided.
                </label>

                <div className="flex flex-wrap justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1.5"
                        data-testid="unused-cancel"
                    >
                        <X className="w-3.5 h-3.5" /> Cancel completion
                    </button>
                    <button
                        type="button"
                        onClick={onReview}
                        disabled={busy}
                        className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-sm inline-flex items-center gap-1.5"
                        data-testid="unused-review"
                    >
                        <Eye className="w-3.5 h-3.5" /> Review tags
                    </button>
                    <button
                        type="button"
                        onClick={onVoidAndComplete}
                        disabled={busy || !confirmed}
                        className="px-3 py-2 text-sm font-semibold bg-red-700 text-white rounded-sm hover:bg-red-800 disabled:bg-gray-300 inline-flex items-center gap-1.5"
                        data-testid="unused-void-and-complete"
                    >
                        <Ban className="w-3.5 h-3.5" />
                        {busy ? "Voiding & completing…" : "Void Unused Tags & Complete Order"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
